// Small, dependency-free interactions with explicit Turbolinks teardown.
let cleanups = [];
const cleanup = () => {
  cleanups.forEach(dispose => dispose());
  cleanups = [];
};
const listen = (target, event, handler) => {
  target.addEventListener(event, handler);
  cleanups.push(() => target.removeEventListener(event, handler));
};

const initialize = () => {
  cleanup();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  document.querySelectorAll('[data-hero-slider]').forEach(root => {
    const slides = [...root.querySelectorAll('[data-hero-slide]')];
    const images = [...root.querySelectorAll('[data-backdrop]')];
    const dots = [...root.querySelectorAll('[data-slide-index]')];
    const pause = root.querySelector('[data-slide-pause]');
    const region = root.querySelector('[data-slide-region]');
    const controls = root.querySelector('[data-slider-controls]');
    let index = 0;
    let timer;
    let paused = reducedMotion.matches;
    let hovered = false;
    let visible = true;
    controls.hidden = false;

    const stopTimer = () => window.clearTimeout(timer);
    const schedule = () => {
      stopTimer();
      const playing = !paused && !hovered && visible && !document.hidden;
      root.classList.toggle('is-playing', playing);
      pause.textContent = paused ? 'Play' : 'Pause';
      pause.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      region.setAttribute('aria-live', paused ? 'polite' : 'off');
      if (playing) timer = window.setTimeout(() => show(index + 1), 7000);
    };
    const show = (next, manual = false) => {
      if (manual) paused = true;
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => { slide.hidden = i !== index; });
      images.forEach((img, i) => {
        img.hidden = i !== index;
        img.classList.toggle('is-active', i === index);
      });
      dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === index)));
      schedule();
    };
    listen(root.querySelector('[data-slide-prev]'), 'click', () => show(index - 1, true));
    listen(root.querySelector('[data-slide-next]'), 'click', () => show(index + 1, true));
    dots.forEach((dot, i) => listen(dot, 'click', () => show(i, true)));
    listen(pause, 'click', () => { paused = !paused; schedule(); });
    listen(root, 'mouseenter', () => { hovered = true; schedule(); });
    listen(root, 'mouseleave', () => { hovered = false; schedule(); });
    // Stop on keyboard focus; only an explicit Play action restarts rotation.
    listen(root, 'focusin', event => {
      if (event.target !== pause) { paused = true; schedule(); }
    });
    listen(document, 'visibilitychange', schedule);
    listen(reducedMotion, 'change', () => { paused = true; schedule(); });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; schedule(); });
      observer.observe(root);
      cleanups.push(() => observer.disconnect());
    }
    show(0);
    cleanups.push(() => {
      stopTimer();
      controls.hidden = true;
      root.classList.remove('is-playing');
      slides.forEach((slide, i) => { slide.hidden = i !== 0; });
      images.forEach((img, i) => { img.hidden = i !== 0; });
    });
  });

  document.querySelectorAll('[data-service-carousel], [data-catalog-carousel]').forEach(root => {
    const track = root.querySelector('.service-carousel-track, .catalog-carousel-track');
    const controls = root.querySelector('[data-carousel-controls]');
    const prev = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    controls.hidden = false;
    const update = () => {
      prev.disabled = track.scrollLeft < 2;
      next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    };
    const move = direction => {
      const card = track.querySelector('.service-card, .product-card');
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({left: direction * (card.getBoundingClientRect().width + gap), behavior: reducedMotion.matches ? 'auto' : 'smooth'});
    };
    listen(prev, 'click', () => move(-1));
    listen(next, 'click', () => move(1));
    listen(track, 'scroll', update);
    listen(track, 'keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }
    });
    listen(window, 'resize', update);
    const expand = root.querySelector('[data-catalog-expand]');
    if (expand) listen(expand, 'click', () => {
      const expanded = root.classList.toggle('catalog-expanded');
      expand.setAttribute('aria-expanded', String(expanded));
      expand.textContent = expanded ? 'Show slider' : 'View all services';
      if (!expanded) track.scrollTo({ left: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      update();
    });
    update();
    cleanups.push(() => { controls.hidden = true; });
  });

  document.querySelectorAll('[data-ticker]').forEach(root => {
    const button = root.querySelector('[data-ticker-pause]');
    let paused = reducedMotion.matches;
    button.hidden = false;
    const update = () => {
      root.classList.toggle('ticker-running', !paused && !reducedMotion.matches);
      button.textContent = paused ? 'Play' : 'Pause';
      button.setAttribute('aria-label', paused ? 'Play moving text' : 'Pause moving text');
      button.hidden = reducedMotion.matches;
    };
    listen(button, 'click', () => { paused = !paused; update(); });
    listen(reducedMotion, 'change', () => { paused = reducedMotion.matches; update(); });
    update();
    cleanups.push(() => { root.classList.remove('ticker-running'); button.hidden = true; });
  });

  if (!reducedMotion.matches && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal-in');
          observer.unobserve(entry.target);
        }
      });
    }, {threshold: 0.12});
    document.querySelectorAll('.journey-step, .section-heading, .info-card, .team-card, .service-content-section').forEach(el => observer.observe(el));
    cleanups.push(() => {
      observer.disconnect();
      document.querySelectorAll('.reveal-in').forEach(el => el.classList.remove('reveal-in'));
    });
  }
};
document.addEventListener('DOMContentLoaded', initialize);
document.addEventListener('turbolinks:load', initialize);
document.addEventListener('turbolinks:before-cache', cleanup);
